import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { format } from 'date-fns';

import { getDateFnsLocale } from 'Helpers/localeService';

import Badge from 'CommonComponents/Badge';
import Button from 'CommonComponents/Button';
import DatePickerField from 'CommonComponents/DatePickerField';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import PerPageSelect from 'CommonComponents/PerPageSelect';
import SectionTile from 'CommonComponents/SectionTile';
import SegmentedControl from 'CommonComponents/SegmentedControl';
import SelectField from 'CommonComponents/SelectField';
import TablePagination from 'CommonComponents/TablePagination';
import TableRowSkeleton from 'CommonComponents/TableRowSkeleton';
import TextButton from 'CommonComponents/TextButton';
import Tooltip from 'CommonComponents/Tooltip';

import useApiRequest from 'Hooks/useApiRequest';
import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import usePagination from 'Hooks/usePagination';
import { useToast } from 'Hooks/useToast';

import CalendarIcon from 'Assets/icons/calendar.svg';
import DotsIcon from 'Assets/icons/dots-horizontal.svg';
import DownloadIcon from 'Assets/icons/download.svg';
import EyeIcon from 'Assets/icons/eye.svg';
import RefreshSyncIcon from 'Assets/icons/refresh-sync.svg';
import TrashIcon from 'Assets/icons/trash.svg';
import WarningIcon from 'Assets/icons/warning-triangle.svg';

const STATUS_OPTIONS = [
    { value: '', label: $t('All') },
    { value: 'created', label: $t('Created') },
    { value: 'in_progress', label: $t('Generating') },
    { value: 'failed', label: $t('Failed') },
    { value: 'completed', label: $t('Complete') },
];

const formatDateTime = (value) => (value ? format(new Date(value), 'd MMM yyyy, HH:mm', { locale: getDateFnsLocale() }) : '-');

/**
 * "Generated Reports" tab: the unified, cross-module report list.
 *
 * Adapts the ArchivedActualCostsReports pattern (Echo `.report.generated`
 * refetch + usePagination + useApiRequest + SectionTile) and the audit-log
 * filter pattern (shared SelectField / DatePickerField inputs). Standard users
 * see only their own rows; super-admins get a My-reports / All-users segmented
 * switch plus a user filter. Each row exposes a state-aware overflow menu
 * (View / Download / Re-run / Delete).
 */
export default function GeneratedReportsList({
    indexUrl,
    deleteUrl,
    downloadUrl,
    channelName,
    actualsChannelName,
    isSuperAdmin = false,
    perPage: initialPerPage = 25,
    pruningDays,
    modules = [],
    subCategories = {},
    onReRun,
    onGoToReportTab,
    onGoToSchedules,
}) {
    const pagination = usePagination({ perPage: initialPerPage });
    const toast = useToast();
    const confirm = useConfirmationModal();

    const [filters, setFilters] = useState({ name: '', module: '', functional_category: '', status: '', from: '', to: '', user: '' });
    const [scope, setScope] = useState('my');

    const reportsRequest = useApiRequest({ url: indexUrl });
    const deleteRequest = useApiRequest({ url: deleteUrl });

    const queryParams = useMemo(
        () => ({
            page: pagination.page,
            per_page: pagination.perPage,
            ...(filters.name ? { name: filters.name } : {}),
            ...(filters.module ? { module: filters.module } : {}),
            ...(filters.functional_category ? { functional_category: filters.functional_category } : {}),
            ...(filters.status ? { status: filters.status } : {}),
            ...(filters.from ? { from: filters.from } : {}),
            ...(filters.to ? { to: filters.to } : {}),
            ...(isSuperAdmin ? { scope } : {}),
            ...(isSuperAdmin && filters.user ? { user: filters.user } : {}),
        }),
        [pagination.page, pagination.perPage, filters, isSuperAdmin, scope],
    );

    const getReports = useCallback(() => {
        reportsRequest.get(queryParams);
    }, [queryParams]);

    // Keep a stable ref to the latest getReports so the Echo effect can call
    // it without re-subscribing on every filter/page change (Fix 3 + Fix 4).
    const getReportsRef = useRef(getReports);
    useEffect(() => {
        getReportsRef.current = getReports;
    });

    // Fetch whenever filters, page, or scope change. Depends on getReports,
    // which already encapsulates all relevant state via queryParams (Fix 4).
    useEffect(() => {
        getReports();
    }, [getReports]);

    useEffect(() => {
        if (reportsRequest.data?.meta) {
            const { meta } = reportsRequest.data;
            pagination.setFrom(meta.from);
            pagination.setTo(meta.to);
            pagination.setTotal(meta.total);
            pagination.setLastPage(meta.last_page);
        }
    }, [reportsRequest.data]);

    // Live status updates; Refresh is the manual fallback. The list aggregates
    // rows from multiple sources: native ReportGeneration rows broadcast on
    // channelName, while bridged rows (e.g. Actuals vs Budget) broadcast on the
    // ActualCostsReport channel. Subscribe to both so either source refreshes
    // the list live. Depends only on the channel names so the subscriptions are
    // never torn down on a filter/page change. The ref gives each listener
    // access to the latest getReports without re-subscribing (Fix 3).
    useEffect(() => {
        if (!window.Echo) {
            return undefined;
        }

        const subscribed = [channelName, actualsChannelName].filter(Boolean);

        subscribed.forEach((name) => {
            window.Echo.private(name).listen('.report.generated', () => getReportsRef.current());
        });

        return () => subscribed.forEach((name) => window.Echo.leave(name));
    }, [channelName, actualsChannelName]);

    const setFilter = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        pagination.setPage(1);
    };

    const moduleOptions = useMemo(() => [{ value: '', label: $t('All') }, ...modules.map((module) => ({ value: module, label: module }))], [modules]);

    // Management Reports functional sub-categories (budget/workforce/raido); the
    // labels arrive already keyed by the backend taxonomy.
    const subCategoryOptions = useMemo(
        () => [{ value: '', label: $t('All') }, ...Object.entries(subCategories || {}).map(([value, label]) => ({ value, label: $t(label) }))],
        [subCategories],
    );

    const deleteReport = (row) => {
        // Delete against the row's own endpoint. A bridged row (e.g. Actuals vs
        // Budget) carries its source-specific deleteUrl, so it never hits the
        // report_generations endpoint with a colliding numeric id. Native rows
        // fall back to the deleteUrl template with the row id substituted in.
        const resolvedDeleteUrl = row.deleteUrl || (deleteUrl ? deleteUrl.replace('REPORT_GENERATION_ID', row.id) : null);

        confirm({
            title: $t('Delete report?'),
            message: $t('This permanently removes the report record and its generated files. This cannot be undone.'),
            actions: [
                { confirmed: false, label: $t('Cancel'), variant: 'secondary', onClick: () => {} },
                {
                    confirmed: true,
                    label: $t('Delete permanently'),
                    variant: 'danger',
                    onClick: () => {
                        deleteRequest
                            .request({ method: 'delete', url: resolvedDeleteUrl })
                            .then(() => {
                                // Deleting the last row on a page beyond the first
                                // would leave it empty — step back a page instead.
                                const currentPageRowCount = reportsRequest.data?.data?.length ?? 0;
                                if (pagination.page > 1 && currentPageRowCount === 1) {
                                    pagination.setPage(pagination.page - 1);
                                } else {
                                    getReports();
                                }
                            })
                            .catch(() => toast.error($t('Failed to delete the report. Please try again.')));
                    },
                },
            ],
        });
    };

    const rows = reportsRequest.data?.data || [];
    const total = reportsRequest.data?.meta?.total ?? 0;
    const columnCount = isSuperAdmin ? 8 : 7;

    return (
        <div className="tw-space-y-4">
            <SectionTile header={$t('Generated Reports')} defaultCollapsed={false} visibly>
                <p className="tw-text-sm tw-text-charcoal-75 tw-mb-4">
                    {$t('Reports older than {{days}} days are deleted automatically.', { days: pruningDays })}
                </p>

                <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3 tw-mb-4">
                    <FilterField label={$t('Report Name')}>
                        <input
                            type="text"
                            className="text-input !tw-h-9"
                            value={filters.name}
                            onChange={(event) => setFilter('name', event.target.value)}
                            placeholder={$t('Report Name')}
                        />
                    </FilterField>

                    <FilterField label={$t('Module / Source')} className="tw-w-48">
                        <SelectField
                            className="tw-block tw-w-full tw-rounded-lg tw-text-sm !tw-bg-grey/50 tw-text-charcoal tw-font-semibold placeholder:tw-text-charcoal-75 placeholder:tw-text-sm placeholder:tw-font-normal"
                            backgroundColor="#e4e4e480"
                            value={filters.module}
                            options={moduleOptions}
                            onChange={(option) => setFilter('module', option ? option.value : '')}
                        />
                    </FilterField>

                    <FilterField label={$t('Sub-category')} className="tw-w-44">
                        <SelectField
                            className="tw-block tw-w-full tw-rounded-lg tw-text-sm !tw-bg-grey/50 tw-text-charcoal tw-font-semibold placeholder:tw-text-charcoal-75 placeholder:tw-text-sm placeholder:tw-font-normal"
                            backgroundColor="#e4e4e480"
                            value={filters.functional_category}
                            options={subCategoryOptions}
                            onChange={(option) => setFilter('functional_category', option ? option.value : '')}
                        />
                    </FilterField>

                    <FilterField label={$t('Status')} className="tw-w-44">
                        <SelectField
                            className="tw-block tw-w-full tw-rounded-lg tw-text-sm !tw-bg-grey/50 tw-text-charcoal tw-font-semibold placeholder:tw-text-charcoal-75 placeholder:tw-text-sm placeholder:tw-font-normal"
                            backgroundColor="#e4e4e480"
                            value={filters.status}
                            options={STATUS_OPTIONS}
                            onChange={(option) => setFilter('status', option ? option.value : '')}
                        />
                    </FilterField>

                    <FilterField label={$t('Generated')}>
                        <DatePickerField
                            name="from"
                            value={filters.from || null}
                            valueFormat="yyyy-MM-dd"
                            className="text-input !tw-h-9"
                            onChange={(name, value) => setFilter('from', value || '')}
                        />
                    </FilterField>

                    {isSuperAdmin && (
                        <Fragment>
                            <FilterField label={$t('User')}>
                                <input
                                    type="text"
                                    className="text-input !tw-h-9"
                                    value={filters.user}
                                    onChange={(event) => setFilter('user', event.target.value)}
                                    placeholder={$t('User')}
                                />
                            </FilterField>

                            <FilterField label="&nbsp;">
                                <SegmentedControl
                                    value={scope}
                                    onChange={setScope}
                                    options={[
                                        { value: 'my', label: $t('My reports') },
                                        { value: 'all', label: $t('All users') },
                                    ]}
                                />
                            </FilterField>
                        </Fragment>
                    )}

                    <Button variant="secondary" small onClick={getReports} className="tw-ms-auto tw-flex tw-items-center tw-gap-1.5">
                        <RefreshSyncIcon className="tw-w-3.5 tw-h-3.5" />
                        {$t('Refresh')}
                    </Button>
                </div>

                <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                    <div className="table__pagination-info">
                        <PaginationInfo to={pagination.to} from={pagination.from} total={pagination.total} />
                        <TablePagination
                            lastPage={pagination.lastPage}
                            pageChange={(data) => pagination.setPage(+data.selected + 1)}
                            currentPage={pagination.page}
                        />
                    </div>
                    <PerPageSelect
                        value={pagination.perPage}
                        options={[10, 25, 50, 100]}
                        onChange={(event) => pagination.setPerPage(parseInt(event.target.value, 10))}
                    />
                </div>

                <div className="inbud-table_wrapper">
                    <table className="inbud-table">
                        <thead>
                            <tr>
                                <th>{$t('Report Name')}</th>
                                <th>{$t('Module / Source')}</th>
                                <th>{$t('Type')}</th>
                                <th>{$t('Source')}</th>
                                <th className="tw-w-40 tw-min-w-40">{$t('Generated')}</th>
                                {isSuperAdmin && <th className="tw-w-40 tw-min-w-40">{$t('User')}</th>}
                                <th className="tw-w-32 tw-min-w-32">{$t('Status')}</th>
                                <th className="tw-w-24 tw-min-w-24 tw-text-end">{$t('Actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reportsRequest.loading && <TableRowSkeleton rows={5} columns={columnCount} />}

                            {!reportsRequest.loading &&
                                rows.map((row) => (
                                    <tr key={row.rowKey || row.id}>
                                        <td>{row.name}</td>
                                        <td>{row.module}</td>
                                        <td>{row.reportTypeLabel || row.type}</td>
                                        <td>
                                            {row.sourceScheduleId ? (
                                                <span className="tw-inline-flex tw-items-center tw-gap-1.5">
                                                    <Badge variant="info">{$t('Scheduled')}</Badge>
                                                    {row.scheduleName && <span className="tw-text-sm tw-text-charcoal-75">{row.scheduleName}</span>}
                                                </span>
                                            ) : (
                                                row.filterSetName || '-'
                                            )}
                                        </td>
                                        <td className="inbud-date-cell">{formatDateTime(row.generatedAt)}</td>
                                        {isSuperAdmin && <td>{row.userName}</td>}
                                        <td>
                                            <Badge status={row.statusKey}>{row.statusLabel}</Badge>
                                        </td>
                                        <td className="tw-text-center">
                                            <RowMenu
                                                row={row}
                                                downloadUrl={downloadUrl}
                                                onReRun={() => onReRun?.(row)}
                                                onOpenSchedule={row.sourceScheduleId ? () => onGoToSchedules?.() : null}
                                                onDelete={() => deleteReport(row)}
                                            />
                                        </td>
                                    </tr>
                                ))}

                            {!reportsRequest.loading && total === 0 && (
                                <tr>
                                    <td colSpan={columnCount}>
                                        <div className="tw-py-12 tw-text-center">
                                            <div className="tw-mx-auto tw-mb-3.5 tw-w-12 tw-h-12 tw-rounded-full tw-bg-charcoal-15" />
                                            <div className="tw-font-medium tw-text-charcoal">{$t('No reports generated yet')}</div>
                                            <div className="tw-mt-1.5 tw-mb-4 tw-text-sm tw-text-charcoal-75">
                                                {$t('Generate a report from the Report tab and it will appear here.')}
                                            </div>
                                            <Button variant="primary" small onClick={onGoToReportTab}>
                                                {$t('Go to Report tab')}
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </SectionTile>
        </div>
    );
}

function FilterField({ label, className, children }) {
    return (
        <div className={className}>
            <label className="text-input__label tw-block tw-mb-1" dangerouslySetInnerHTML={{ __html: label }} />
            {children}
        </div>
    );
}

function RowMenu({ row, downloadUrl, onReRun, onOpenSchedule, onDelete }) {
    const [open, setOpen] = useState(false);
    const [showFormats, setShowFormats] = useState(false);
    const [showError, setShowError] = useState(false);

    const completed = row.statusKey === 'completed';
    const failed = row.statusKey === 'failed';
    const formats = row.supportedFormats || [];
    const resolvedDownloadUrl = row.downloadUrl || (downloadUrl ? downloadUrl.replace('REPORT_GENERATION_ID', row.id) : null);

    const handleDownload = () => {
        if (!resolvedDownloadUrl) {
            return;
        }
        if (formats.length > 1) {
            setOpen(false);
            setShowFormats(true);
            return;
        }
        window.location.href = resolvedDownloadUrl;
        setOpen(false);
    };

    const downloadFormat = (formatKey) => {
        // TODO IB-future: per-format download. The backend currently serves a
        // single artefact; pass the requested format as a hint for when it does.
        const separator = resolvedDownloadUrl.includes('?') ? '&' : '?';
        window.location.href = `${resolvedDownloadUrl}${separator}format=${formatKey}`;
        setShowFormats(false);
    };

    // The menu renders in a portal via the shared Tooltip (react-popper-tooltip),
    // so it is positioned above the table and never clipped by the horizontal
    // scroll container. Controlled visibility lets an item click dismiss it.
    const menu = (
        <div className="tw-flex tw-flex-col tw-min-w-40">
            {/* A draft management pack opens in its builder before any export completes. */}
            {row.viewUrl && (completed || row.canOpen) && (
                <TextButton as="a" href={row.viewUrl} className="tw-flex tw-w-full tw-justify-start tw-px-3 tw-py-2 tw-rounded-lg hover:tw-bg-charcoal-15">
                    <EyeIcon className="tw-w-3.5 tw-h-3.5" />
                    {row.viewLabel ?? $t('View')}
                </TextButton>
            )}
            <TextButton
                onClick={handleDownload}
                disabled={!completed || !resolvedDownloadUrl}
                className="tw-flex tw-w-full tw-justify-start tw-px-3 tw-py-2 tw-rounded-lg hover:tw-bg-charcoal-15 disabled:tw-opacity-40"
            >
                <DownloadIcon className="tw-w-3.5 tw-h-3.5" />
                {$t('Download')}
            </TextButton>
            {failed && (
                <TextButton
                    onClick={() => {
                        setOpen(false);
                        setShowError(true);
                    }}
                    className="tw-flex tw-w-full tw-justify-start tw-px-3 tw-py-2 tw-rounded-lg hover:tw-bg-charcoal-15"
                >
                    <WarningIcon className="tw-w-3.5 tw-h-3.5" />
                    {$t('View error')}
                </TextButton>
            )}
            {/* A run still in the queue or generating would be duplicated. */}
            {(completed || failed) && (
                <TextButton
                    onClick={() => {
                        setOpen(false);
                        onReRun();
                    }}
                    className="tw-flex tw-w-full tw-justify-start tw-px-3 tw-py-2 tw-rounded-lg hover:tw-bg-charcoal-15"
                >
                    <RefreshSyncIcon className="tw-w-3.5 tw-h-3.5" />
                    {$t('Re-run')}
                </TextButton>
            )}
            {onOpenSchedule && (
                <TextButton
                    onClick={() => {
                        setOpen(false);
                        onOpenSchedule();
                    }}
                    className="tw-flex tw-w-full tw-justify-start tw-px-3 tw-py-2 tw-rounded-lg hover:tw-bg-charcoal-15"
                >
                    <CalendarIcon className="tw-w-3.5 tw-h-3.5" />
                    {$t('Open schedule')}
                </TextButton>
            )}
            <TextButton
                variant="danger"
                onClick={() => {
                    setOpen(false);
                    onDelete();
                }}
                className="tw-flex tw-w-full tw-justify-start tw-px-3 tw-py-2 tw-rounded-lg hover:tw-bg-charcoal-15"
            >
                <TrashIcon className="tw-w-3 tw-h-3" />
                {$t('Delete')}
            </TextButton>
        </div>
    );

    return (
        <Fragment>
            <Tooltip
                trigger="click"
                tooltipType="content"
                placement="bottom-end"
                interactive
                visible={open}
                onVisibleChange={setOpen}
                backgroundClass="tw-bg-white tw-border tw-border-grey !tw-opacity-100 !tw-p-1"
                tooltipContent={menu}
            >
                {/* Raw button: the shared Tooltip clones its child to attach the
                    popper trigger ref, which a plain-function IconButton can't
                    forward. Popper's onVisibleChange drives `open` on click. */}
                <button type="button" title={$t('Actions')} aria-label={$t('Actions')}>
                    <DotsIcon className="tw-w-3.5 tw-h-3.5" />
                </button>
            </Tooltip>

            {showFormats && <DownloadFormatModal name={row.name} formats={formats} onSelect={downloadFormat} onClose={() => setShowFormats(false)} />}
            {showError && (
                <ReportErrorModal name={row.name} message={row.failureMessage} reference={row.failureReference} onClose={() => setShowError(false)} />
            )}
        </Fragment>
    );
}

function DownloadFormatModal({ name, formats, onSelect, onClose }) {
    const [selected, setSelected] = useState(formats[0]);

    return (
        <div className="modal__box tw-flex tw-fixed tw-inset-0 tw-z-50 tw-items-center tw-justify-center tw-bg-charcoal-75">
            <div className="tw-relative tw-w-full tw-max-w-sm tw-mx-4">
                <div className="tw-bg-white tw-rounded-lg tw-shadow-primary tw-p-6">
                    <h3 className="tw-text-lg tw-font-semibold tw-text-blue tw-mb-4">{$t('Download — {{name}}', { name })}</h3>
                    <div className="tw-space-y-2 tw-mb-6">
                        {formats.map((formatKey) => (
                            <label key={formatKey} className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                                <input
                                    type="radio"
                                    name="download-format"
                                    value={formatKey}
                                    checked={selected === formatKey}
                                    onChange={() => setSelected(formatKey)}
                                />
                                <span className="tw-text-sm tw-text-charcoal">{formatKey.toUpperCase()}</span>
                            </label>
                        ))}
                    </div>
                    <div className="tw-flex tw-justify-end tw-space-x-1.5">
                        <Button variant="secondary" small onClick={onClose}>
                            {$t('Cancel')}
                        </Button>
                        <Button variant="primary" small onClick={() => onSelect(selected)}>
                            {$t('Download')}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ReportErrorModal({ name, message, reference, onClose }) {
    return (
        <div className="modal__box tw-flex tw-fixed tw-inset-0 tw-z-50 tw-items-center tw-justify-center tw-bg-charcoal-75">
            <div className="tw-relative tw-w-full tw-max-w-sm tw-mx-4">
                <div className="tw-bg-white tw-rounded-lg tw-shadow-primary tw-p-6">
                    <h3 className="tw-text-lg tw-font-semibold tw-text-blue tw-mb-4">{name}</h3>
                    <div className="tw-space-y-2 tw-mb-6">
                        <p className="tw-text-sm tw-text-charcoal">{message}</p>
                        {reference && <p className="tw-text-sm tw-text-charcoal-75">{$t('Reference: {{reference}}', { reference })}</p>}
                    </div>
                    <div className="tw-flex tw-justify-end tw-space-x-1.5">
                        <Button variant="secondary" small onClick={onClose}>
                            {$t('Close')}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
